import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { App as AntApp, Button } from 'antd';
import AppBar from '../components/AppBar';
import { EmptyState, Skeletons } from '../components/kit';
import MemoBody from '../components/memos/MemoBody';
import { memoDate } from '../components/memos/memoFormat';
import * as memosApi from '../api/memos';
import { refreshMemoUnread } from '../hooks/useMemoUnread';
import { refreshUnreadCount } from '../components/NotifBell';

// One memo from HQ — opened from its notification, from More → Memos, or a push.
// A notification-only memo counts as read once it is opened here; a pop-up memo
// still waiting for "I have read this" carries the button at the foot.
export default function MemoDetailPage() {
  const { id } = useParams();
  const { message } = AntApp.useApp();
  const [memo, setMemo] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setMemo(null);
    setError(null);
    memosApi.getMemo(id)
      .then(async (r) => {
        const m = r?.data ?? r;
        if (!live) return;
        setMemo(m);
        if (m.show_as === 'FEED' && !m.read_at && m.live) {
          const res = await memosApi.markRead(m.id).catch(() => null);
          if (live && res) setMemo({ ...m, read_at: (res?.data ?? res)?.read_at || new Date().toISOString() });
          refreshMemoUnread();
          // Reading it also clears the memo's notification on the bell.
          refreshUnreadCount();
        }
      })
      .catch((e) => { if (live) setError(e?.message || 'This memo is no longer available'); });
    return () => { live = false; };
  }, [id]);

  const confirm = async () => {
    setSaving(true);
    try {
      const res = await memosApi.markRead(memo.id);
      setMemo({ ...memo, read_at: (res?.data ?? res)?.read_at || new Date().toISOString() });
      refreshMemoUnread();
      refreshUnreadCount();
    } catch (e) {
      if (e?.status === 404 || e?.status === 410) {
        // HQ withdrew it while it was open here.
        setMemo({ ...memo, live: false });
        message.info('This memo was withdrawn by HQ');
        refreshMemoUnread();
        refreshUnreadCount();
      } else {
        message.error(e?.message || 'Could not record that — please try again');
      }
    } finally {
      setSaving(false);
    }
  };

  const needsConfirm = memo && memo.show_as === 'POPUP' && !memo.read_at && memo.live;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Memo" sub={memo ? `from ${memo.from_label}` : 'from HQ'} />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 24px' }}>
        {error ? (
          <EmptyState title="Memo not available" sub={error} />
        ) : !memo ? (
          <Skeletons n={2} height={120} />
        ) : (
          <>
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{memo.from_label} · {memoDate(memo.publish_at)}</div>
            <div style={{ fontSize: 18, fontWeight: 650, lineHeight: 1.3, margin: '6px 0 12px' }}>{memo.title}</div>
            <MemoBody memo={memo} />
            {memo.read_at && (
              <div style={{ marginTop: 18, fontSize: 12.5, fontWeight: 600, color: 'var(--ok)' }}>✓ You confirmed · {memoDate(memo.read_at)}</div>
            )}
            {!memo.live && !memo.read_at && (
              <div style={{ marginTop: 18, fontSize: 12.5, color: 'var(--muted)' }}>This memo is no longer showing.</div>
            )}
          </>
        )}
      </div>
      {needsConfirm && (
        <div style={{ padding: '14px 16px', paddingBottom: 'calc(14px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none' }}>
          <Button type="primary" block loading={saving} onClick={confirm} style={{ height: 48, fontWeight: 600, fontSize: 15 }}>I have read this</Button>
        </div>
      )}
    </div>
  );
}
